import { useState, useEffect } from 'react';
import { Resource } from '../components/Resource';
import { PrismCode } from '../components/PrismCode';
import { default as Tabs, type TabsProps } from 'antd/es/tabs';
import { default as Skeleton } from 'antd/es/skeleton';
import { loadTxt } from '../utils/loadTxt';
import { Link } from 'react-router-dom';
import { CUSTOM_TAB_STYLE } from '../useCustomAntdTheme';
import { enhanceLinkWithToken } from '../utils/enhanceLinkWithToken';

export function SignAndEncryptPage() {
  const [tabs, setTabs] = useState<TabsProps['items']>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTabs = async () => {
      const newTabs = [
        {
          key: 'ruby',
          label: 'Ruby',
          children: (
            <PrismCode
              showClipboard
              language="ruby"
              code={(await loadTxt('encrypt-ruby')) ?? ''}
            />
          )
        },
        {
          key: 'bash',
          label: 'Bash (OpenSSL)',
          children: (
            <PrismCode
              showClipboard
              language="bash"
              code={(await loadTxt('encrypt-bash')) ?? ''}
            />
          )
        },
        {
          key: 'php',
          label: 'PHP',
          children: (
            <PrismCode
              showClipboard
              language="php"
              code={(await loadTxt('encrypt-php')) ?? ''}
            />
          )
        },
        {
          key: 'python',
          label: 'Python',
          children: (
            <PrismCode
              showClipboard
              language="python"
              code={(await loadTxt('encrypt-python')) ?? ''}
            />
          )
        },
        {
          key: 'dotnet',
          label: '.NET',
          children: (
            <PrismCode
              showClipboard
              language="cs"
              code={(await loadTxt('encrypt-dotnet')) ?? ''}
            />
          )
        },
        {
          key: 'java',
          label: 'Java',
          children: (
            <PrismCode
              showClipboard
              language="java"
              code={(await loadTxt('encrypt-java')) ?? ''}
            />
          )
        },
        {
          key: 'go',
          label: 'Go',
          children: (
            <PrismCode
              showClipboard
              language="go"
              code={(await loadTxt('encrypt-go')) ?? ''}
            />
          )
        },
        {
          key: 'node',
          label: 'Node.js',
          children: (
            <PrismCode
              showClipboard
              language="js"
              code={(await loadTxt('encrypt-nodejs')) ?? ''}
            />
          )
        }
      ];

      setTabs(newTabs);
      setIsLoading(false);
    };

    loadTabs();
  }, []);
  if (isLoading) {
    return <Skeleton active />;
  }

  return (
    <div className="raw flex flex-row flex-wrap gap-8">
      <div className="flex w-1/2 flex-1 flex-col gap-4 whitespace-pre-line">
        <Resource
          resource={{ filePath: 'api/getting-started/sign-and-encrypt' }}
        />
        <div>
          <PrismCode dataSrc="pkcs7example" />
        </div>
        <div className="warning">
          <li>
            <b>Important:</b> The signed and encrypted data should be send as
            the request body.
          </li>
          <li>
            Content type of the request should be text/plain
            (Content-Type=text/plain).
          </li>
          <li>
            <Link
              to={enhanceLinkWithToken(
                '/guide/docs-integration#integration-needed-setup'
              )}
              target="_blank"
            >
              Click here if you are unsure how to get these keys?
            </Link>
          </li>
          <li>
            <Link
              to={enhanceLinkWithToken(
                '/guide/docs-integration#integration-authentication'
              )}
              target="_blank"
            >
              Read more about the encryption and authentication model here
            </Link>
          </li>
        </div>
      </div>
      <div className="flex w-1/2 flex-1 flex-col gap-5">
        <Tabs items={tabs} title="EXAMPLES" style={CUSTOM_TAB_STYLE} />
      </div>
    </div>
  );
}
